import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import {
  MatchAggregated,
  Round,
} from 'functions/src/types/firestore/Game/Tournament/Bracket';
import { MatchProps } from '../../match/MatchProvider';
import { Team } from 'functions/src/types/firestore/Game/Tournament/Guestlist';
import { CompetitorOutcomeProps } from '../../CompetitorOutcome';

export function splitRound(round: Round<Date>) {
  const matches = round.matches || [];
  const halfLength = matches.length / 2;
  const quarterLength = halfLength / 2;
  const matchesCenter = matches.length === 1 && matches;
  const matchesLeft = !!matchesCenter
    ? undefined
    : matches.length === 2
    ? [matches[0]]
    : matches
        .slice(0, quarterLength)
        .concat(matches.slice(halfLength, halfLength + quarterLength));
  const matchesRight = !!matchesCenter
    ? undefined
    : matches.length === 2
    ? [matches[1]]
    : matches
        .slice(quarterLength, halfLength)
        .concat(matches.slice(halfLength + quarterLength));
  return [
    { ...round, matches: matchesLeft },
    { ...round, matches: matchesRight },
    !!matchesCenter ? { ...round, matches: matchesCenter } : undefined,
  ];
}

export function splitRoundLoser(round: Round<Date>) {
  const matches = round.matches || [];
  const matchesHalf = Math.ceil(matches.length / 2);
  const matchesCenter = matches.length === 1 && matches;
  const matchesLeft = !!matchesCenter
    ? undefined
    : matches.slice(0, matchesHalf);
  const matchesRight = !!matchesCenter ? undefined : matches.slice(matchesHalf);

  return [
    { ...round, matches: matchesLeft },
    { ...round, matches: matchesRight },
    !!matchesCenter ? { ...round, matches: matchesCenter } : undefined,
  ];
}

export function groupRounds(rounds: Round<Date>[], isLoser = false) {
  const roundsLeft: (Round<Date> | undefined)[] = [];
  const roundsRight: (Round<Date> | undefined)[] = [];
  const roundsCenter: Round<Date>[] = [];

  rounds.forEach((curr) => {
    const [currLeft, currRight, currCenter] = isLoser
      ? splitRoundLoser(curr)
      : splitRound(curr);
    roundsLeft.push(currLeft);
    roundsRight.push(currRight);
    if (!!currCenter) {
      roundsCenter.push(currCenter);
    }
  });

  return [roundsLeft, roundsRight, roundsCenter];
}

export function groupMatchesLoser(rounds: Round<Date>[]) {
  const mergedRoundMatches: { matches: MatchAggregated<Date>[] }[] = [];

  for (let i = 0; i < rounds.length; i += 2) {
    const round1 = rounds[Number(i)];
    const round2 = rounds[i + 1];
    const mergedMatches: MatchAggregated<Date>[] = [];
    if (!!round1.matches && !!round2.matches) {
      for (
        let j = 0;
        j < round1.matches.length || j < round2.matches.length;
        j++
      ) {
        if (j < round1.matches.length) {
          mergedMatches.push(round1.matches[Number(j)]);
        }
        if (j < round2.matches.length) {
          mergedMatches.push(round2.matches[Number(j)]);
        }
      }
      if (mergedMatches.length > 0) {
        mergedRoundMatches.push({ matches: mergedMatches });
      }
    }
  }

  return mergedRoundMatches;
}

export function toMatchPairs(matchesProps) {
  return matchesProps.map((matchGroup) => {
    const matchPairs: MatchAggregated[][] = [];
    for (let i = 0; i < matchGroup.matches.length; i += 2) {
      const match1 = matchGroup.matches[Number(i)];
      const match2 = matchGroup.matches[i + 1];
      matchPairs.push([match1, match2]);
    }
    return matchPairs;
  });
}

export function toOutcome(
  forTeam?: Team<Date> | null,
  otherTeam?: Team<Date> | null,
  winner?: Team<Date> | null,
): CompetitorOutcomeProps['outcome'] {
  if (!winner || !forTeam) {
    return undefined;
  }
  if (winner.id === forTeam.id) {
    return otherTeam ? 'win' : 'bypass';
  }
  return 'loss';
}

export function convertToMatchCellProps(
  matchesGroup: {
    matches: MatchAggregated<Date>[];
  }[],
  MatchCell: (props: MatchProps) => JSX.Element,
  isCenter = false,
) {
  return matchesGroup.map((matchGroup, indexGroup) => {
    const matchesToConvert = matchGroup.matches;
    return {
      matches: matchesToConvert.map((match, index) => {
        const isFinal =
          isCenter &&
          index === matchesToConvert.length - 1 &&
          indexGroup === matchesGroup.length - 1;
        return {
          matchId: match.id,
          match: MatchCell({
            ...match,
            isLarge: !!isFinal,
          }),
        };
      }),
    };
  });
}

export function toColumnsLoser(columnsGroup, position?: string) {
  const isCenter = position === 'center';
  const isLeft = position === 'left';

  return columnsGroup.map((column, indexColumn) => {
    const shiftDownColumn = isCenter
      ? 0
      : indexColumn === (isLeft ? columnsGroup.length - 1 : 0);
    const shiftDownPair =
      columnsGroup.length === 3 &&
      ((!!isLeft && indexColumn > 0) ||
        (!isLeft && indexColumn < columnsGroup.length - 1));
    return (
      <Stack
        key={`${column[0][0].matchId}`}
        direction="column"
        alignSelf={!shiftDownPair ? 'center' : 'flex-end'}
        alignItems="center"
        spacing={8}
        sx={{
          mt: shiftDownColumn ? '218px !important' : 0,
        }}
      >
        {column.map((matchPair, indexPair) => {
          const tuckIn = columnsGroup.length === 3 && column.length === 1;
          const flipPair = shiftDownPair && (indexPair === 1 || tuckIn);
          return (
            <Stack
              key={matchPair[0].matchId}
              direction={!!isCenter || !!flipPair ? 'column-reverse' : 'column'}
              alignItems="center"
              spacing={8}
              mb={shiftDownPair ? '112px !important' : 0}
              ml={
                isLeft && tuckIn
                  ? '48px !important'
                  : !isLeft && tuckIn
                  ? '-48px'
                  : 0
              }
              mr={isLeft && tuckIn ? '-48px' : !isLeft && tuckIn ? '48px' : 0}
              height="100%"
              width={!!isCenter ? '180px' : '100%'}
            >
              <Box>{matchPair[0].match}</Box>
              <Box
                sx={{
                  height: 'fit-content',
                  mr: isLeft ? '-108px !important' : 0,
                  ml: !isLeft && !isCenter ? '-108px !important' : 0,
                }}
              >
                {matchPair[1].match}
              </Box>
            </Stack>
          );
        })}
      </Stack>
    );
  });
}

export function groupMatches(rounds: Round<Date>[]) {
  // eslint-disable-next-line @blumintinc/blumint/extract-global-constants
  const matchesGrouped: { matches: MatchAggregated<Date>[] }[] = [];
  rounds.reduce((prev, curr) => {
    if (curr?.matches?.length) {
      prev.push({
        matches: curr.matches,
      });
    }
    return prev;
  }, matchesGrouped);
  return matchesGrouped;
}

export function toColumns(
  matchesGroup: {
    matches: { matchId: string; match: JSX.Element }[];
  }[],
  position: string,
  roundsNum: number,
) {
  const isLeft = position === 'left';
  const isCenter = position === 'center';
  const isRight = position === 'right';
  const isFullBracket = roundsNum === 5;
  const isSemiFullBracket = roundsNum === 4;
  const isFinal = roundsNum === 1;
  return matchesGroup.map((column, index) => {
    const spaceBetween =
      (isLeft && index === matchesGroup.length - 1) || (isRight && index === 0);
    const spaceOut =
      (isLeft && index === 0) || (isRight && index === matchesGroup.length - 1);
    return (
      <Stack
        direction="column"
        width={isCenter ? '160px' : 'auto'}
        key={column.matches[0].matchId}
        alignItems="center"
        justifyContent={
          isSemiFullBracket || isCenter || spaceBetween
            ? 'space-between'
            : 'space-around'
        }
        spacing={!isFullBracket ? 24 : 4}
        pb={
          isFullBracket && (isCenter || spaceBetween)
            ? '48px'
            : isFinal
            ? 12
            : 0
        }
        pt={
          isFullBracket && (isCenter || spaceBetween)
            ? 68
            : isSemiFullBracket && !spaceOut
            ? 56
            : isFinal
            ? 40
            : 0
        }
      >
        {column.matches.map((matchObj) => {
          return matchObj.match;
        })}
      </Stack>
    );
  });
}
